import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AppNav from "./AppNav";
import Header from "./Header";
import { PhoneAccountProvider } from "../../contexts/PhoneAccountContext";
import { LiveChatProvider } from "../../contexts/LiveChatContext";
import { TimezoneProvider } from "../../contexts/TimezoneContext";
import { isAuthenticated, clearAuthAndRedirectToLogin } from "../../utils/auth";
import { initSocket } from "../../utils/socket";
import useSubscriptionAccess from "../../hooks/useSubscriptionAccess";

import ThemeLoader from "../ui/ThemeLoader";
import WalletBalanceBanner from "../WalletBalanceBanner";
import { useBranding } from "../../contexts/BrandingContext";

const BLEED_PREFIXES = ["/live-chats", "/flow-builder", "/flows/builder", "/api-docs", "/whatsapp-flows"];

const Layout = ({ children }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: accessLoading, hasAccess } = useSubscriptionAccess();
  const brand = useBranding();
  const brandName = String(brand?.name || "").trim();

  // First entry after login/signup without a plan: take the user to the
  // subscribe page once per session so plans are impossible to miss.
  useEffect(() => {
    if (accessLoading || hasAccess) return;
    if (location.pathname === "/subscribe") return;
    if (sessionStorage.getItem("subscribe_prompted")) return;
    sessionStorage.setItem("subscribe_prompted", "1");
    navigate("/subscribe", { replace: false });
  }, [accessLoading, hasAccess, location.pathname, navigate]);

  useEffect(() => {
    const checkUser = () => {
      if (!isAuthenticated()) {
        clearAuthAndRedirectToLogin();
      } else {
        initSocket();
        setTimeout(() => setIsLoading(false), 500);
      }
    };
    checkUser();
  }, [navigate]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1080) setPanelOpen(false);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F0F2F5] flex flex-col items-center justify-center">
        <ThemeLoader
          size="lg"
          message={brandName ? `Loading ${brandName}...` : "Loading..."}
          submessage="Please wait while your dashboard is prepared"
        />
      </div>
    );
  }

  const bleed = BLEED_PREFIXES.some((p) => location.pathname.startsWith(p));

  return (
    <TimezoneProvider>
      <PhoneAccountProvider>
        <LiveChatProvider>
          <div className="ping-app">
            <AppNav panelOpen={panelOpen} setPanelOpen={setPanelOpen} />
            <div className="ping-main">
              <Header toggleSidebar={() => setPanelOpen((v) => !v)} />
              <WalletBalanceBanner />
              <div className={`ping-content ${bleed ? "bleed" : ""}`}>
                <div className={bleed ? "" : "ping-screen"}>{children}</div>
              </div>
            </div>
          </div>
        </LiveChatProvider>
      </PhoneAccountProvider>
    </TimezoneProvider>
  );
};

export default Layout;
